import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';

/**
 * Panel derecho: header, mensajes y input. Modelo user-to-user — sin botón
 * de oferta, sin banner. Lo que sí: cuando el mensaje tiene payload.productId
 * o payload.reservationId, renderea la tarjeta correspondiente (producto vs
 * reserva). El sender también puede tener un producto/reserva "adjunto"
 * arriba del input que va a ir en el próximo mensaje (X para deseleccionar).
 */
const RESERVATION_STATUS_LABELS = {
  ACTIVE: 'Pendiente',
  ACCEPTED: 'Confirmada',
  REJECTED: 'Rechazada',
  WITHDRAWN: 'Cancelada',
  EXPIRED: 'Expirada',
};

const RESERVATION_STATUS_STYLES = {
  ACTIVE:    'bg-primary/10 text-primary',
  ACCEPTED:  'bg-emerald-100 text-emerald-700',
  REJECTED:  'bg-error/10 text-error',
  WITHDRAWN: 'bg-on-surface-variant/10 text-on-surface-variant',
  EXPIRED:   'bg-on-surface-variant/10 text-on-surface-variant',
};

const MessageThread = ({
  activeConv,
  messages,
  currentUserId,
  isConnected,
  inputText,
  onInputChange,
  onKeyPress,
  onSendMessage,
  onBack,
  getOtherParticipantName,
  formatTime,
  newMessagesDividerIndex,
  messagesEndRef,
  attachedProductId,
  onClearAttachedProduct,
  productCache,
  attachedReservationId,
  onClearAttachedReservation,
  reservationCache,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!activeConv) {
    return (
      <div
        className="flex-1 flex items-center justify-center px-6"
        style={{ backgroundColor: 'var(--surface)' }}
      >
        <div className="text-center max-w-sm">
          <div
            className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgb(var(--cm-primary) / 0.1)' }}
          >
            <span
              className="material-symbols-outlined text-[40px]"
              style={{ color: 'rgb(var(--cm-primary))', fontVariationSettings: "'FILL' 1" }}
            >
              forum
            </span>
          </div>
          <p className="text-base font-semibold text-on-surface-variant">
            {t('Elegí una conversación para empezar')}
          </p>
        </div>
      </div>
    );
  }

  const accent = 'rgb(var(--cm-primary))';

  const renderProductCard = (productId, options = {}) => {
    const { compact = false } = options;
    const product = productCache?.[String(productId)];
    if (!product) {
      return (
        <div
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-outline-variant/40 bg-surface-container/60"
        >
          <div className="w-9 h-9 rounded-md bg-surface-container-high animate-pulse" />
          <div className="flex-1 min-w-0">
            <div className="h-3 w-24 bg-surface-container-high animate-pulse rounded mb-1" />
            <div className="h-2.5 w-16 bg-surface-container-high animate-pulse rounded" />
          </div>
        </div>
      );
    }
    const imgUrl = product.imageId ? getImageUrl(product.imageId) : null;
    return (
      <button
        type="button"
        onClick={() => navigate(`/products/${product.id ?? productId}`)}
        className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border border-outline-variant/40 bg-surface-container/60 hover:bg-surface-container-high transition-colors w-full text-left"
        title={t('Ver producto')}
      >
        {imgUrl ? (
          <img src={imgUrl} alt="" className={`${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-md object-cover shrink-0 bg-surface-container-high`} />
        ) : (
          <div className={`${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-md bg-surface-container-high flex items-center justify-center shrink-0`}>
            <span className="material-symbols-outlined text-on-surface-variant text-base">shopping_bag</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-on-surface truncate">{product.name}</p>
          {product.price != null && (
            <p className="text-[11px] font-semibold text-primary">
              ${Number(product.price).toLocaleString()}
            </p>
          )}
        </div>
      </button>
    );
  };

  // Tarjeta para una reserva embebida en un mensaje. Visualmente derivada
  // de la del producto pero con badge de estado y link a /reservations
  // (en vez de /products/X). Esto la diferencia: la conversa puede
  // "tratar sobre" una reserva concreta y no sobre el producto suelto.
  const renderReservationCard = (reservationId, options = {}) => {
    const { compact = false } = options;
    const reservation = reservationCache?.[String(reservationId)];
    const product = reservation?.productId
      ? productCache?.[String(reservation.productId)]
      : null;
    if (!reservation) {
      return (
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-outline-variant/40 bg-surface-container/60">
          <div className="w-9 h-9 rounded-md bg-surface-container-high animate-pulse" />
          <div className="flex-1 min-w-0">
            <div className="h-3 w-28 bg-surface-container-high animate-pulse rounded mb-1" />
            <div className="h-2.5 w-16 bg-surface-container-high animate-pulse rounded" />
          </div>
        </div>
      );
    }
    const status = reservation.status || 'ACTIVE';
    const statusLabel = t(RESERVATION_STATUS_LABELS[status] || status);
    const statusStyle = RESERVATION_STATUS_STYLES[status] || RESERVATION_STATUS_STYLES.ACTIVE;
    const imgUrl = product?.imageId ? getImageUrl(product.imageId) : null;
    return (
      <button
        type="button"
        onClick={() => navigate(`/reservations?reservationId=${encodeURIComponent(reservationId)}`)}
        className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border border-outline-variant/40 bg-surface-container/60 hover:bg-surface-container-high transition-colors w-full text-left"
        title={t('Ver esta compraventa')}
      >
        {imgUrl ? (
          <img src={imgUrl} alt="" className={`${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-md object-cover shrink-0 bg-surface-container-high`} />
        ) : (
          <div className={`${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-md bg-surface-container-high flex items-center justify-center shrink-0`}>
            <span className="material-symbols-outlined text-on-surface-variant text-base">swap_horiz</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-black uppercase tracking-wider text-on-surface-variant">{t('Reserva')}</span>
            <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${statusStyle}`}>
              {statusLabel}
            </span>
          </div>
          <p className="text-xs font-bold text-on-surface truncate">
            {product?.name || t('Compraventa #{{id}}', { id: reservationId })}
          </p>
        </div>
      </button>
    );
  };

  const renderMessageContent = (message) => {
    const text = message.payload?.text || '';
    const productId = message.payload?.productId;
    const reservationId = message.payload?.reservationId;
    return (
      <div className="flex flex-col gap-1.5">
        {reservationId
          ? <div className="-mx-0.5">{renderReservationCard(reservationId, { compact: true })}</div>
          : productId && <div className="-mx-0.5">{renderProductCard(productId, { compact: true })}</div>}
        {text && <span className="text-sm leading-relaxed whitespace-pre-wrap">{text}</span>}
      </div>
    );
  };

  const otherName = getOtherParticipantName(activeConv);
  const inputDisabled = !isConnected;
  const canSend = isConnected && (inputText.trim().length > 0 || !!attachedProductId || !!attachedReservationId);

  return (
    <>
      <div className="flex items-center gap-3 px-3 py-2.5 border-b border-outline-variant/30 bg-[var(--surface)]">
        <button
          onClick={onBack}
          className="md:hidden p-1.5 rounded-full hover:bg-surface-container transition-colors"
          aria-label="Back"
        >
          <span className="material-symbols-outlined text-on-surface-variant">arrow_back</span>
        </button>

        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
          style={{ backgroundColor: accent }}
        >
          {otherName.charAt(0).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-on-surface truncate">{otherName}</p>
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto p-3 flex flex-col min-h-0"
        style={{ backgroundColor: 'var(--surface)' }}
      >
        {messages.length === 0 ? (
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-sm">
              <div
                className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgb(var(--cm-primary) / 0.1)' }}
              >
                <span
                  className="material-symbols-outlined text-[32px]"
                  style={{ color: 'rgb(var(--cm-primary))', fontVariationSettings: "'FILL' 1" }}
                >
                  waving_hand
                </span>
              </div>
              <p className="text-base font-semibold text-on-surface">
                {t('Decí hola a {{name}}', { name: otherName })}
              </p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwn = String(message.senderId) === String(currentUserId);
            return (
              <React.Fragment key={message.id}>
                {index === newMessagesDividerIndex && (
                  <div className="flex items-center my-2">
                    <div className="flex-1 border-t border-outline-variant/50" />
                    <span className="px-3 text-xs font-semibold text-primary">{t('Mensajes Nuevos')}</span>
                    <div className="flex-1 border-t border-outline-variant/50" />
                  </div>
                )}
                <div className={`flex mb-3 px-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[88%] tablet-sm:max-w-[70%]">
                    <div
                      className="px-3 py-2 break-words"
                      style={{
                        backgroundColor: isOwn ? '#1976d2' : '#e2e8f0',
                        color: isOwn ? '#fff' : '#0f172a',
                        borderRadius: 16,
                        borderTopLeftRadius: isOwn ? 16 : 4,
                        borderTopRightRadius: isOwn ? 4 : 16,
                        borderBottomLeftRadius: 16,
                        borderBottomRightRadius: 16,
                        border: 'none',
                      }}
                    >
                      {renderMessageContent(message)}
                      <span
                        className="block text-[10px] opacity-70 text-right mt-1"
                        style={{ color: isOwn ? '#fff' : '#475569' }}
                      >
                        {formatTime(message.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Adjunto al próximo mensaje (reserva o producto). reservationId
          tiene prioridad — son mutuamente excluyentes en el state. La X
          deselecciona el adjunto antes de enviar. */}
      {(attachedReservationId || attachedProductId) && (
        <div className="px-3 pt-2 border-t border-outline-variant/30 bg-[var(--surface)]">
          <div className="relative">
            {attachedReservationId
              ? renderReservationCard(attachedReservationId)
              : renderProductCard(attachedProductId)}
            <button
              type="button"
              onClick={attachedReservationId ? onClearAttachedReservation : onClearAttachedProduct}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-surface text-on-surface-variant hover:bg-surface-container-high flex items-center justify-center shadow-sm"
              aria-label={t('Quitar adjunto')}
              title={t('Quitar adjunto')}
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>
          <p className="text-[10px] text-on-surface-variant mt-1 mb-2">
            {t('Se va a enviar junto al próximo mensaje.')}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-outline-variant/30 bg-[var(--surface)]">
        <input
          type="text"
          className="flex-1 px-3 py-2 rounded-xl border border-outline-variant bg-surface-container text-sm text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          placeholder={t('Escribe un mensaje...')}
          value={inputText}
          onChange={onInputChange}
          onKeyPress={onKeyPress}
          disabled={inputDisabled}
        />
        <button
          onClick={onSendMessage}
          disabled={!canSend}
          className="p-2 rounded-full bg-[var(--primary)] text-[var(--on-primary)] disabled:opacity-50 hover:opacity-90 transition-opacity"
          aria-label="Send"
        >
          <span className="material-symbols-outlined text-[20px]">send</span>
        </button>
      </div>
    </>
  );
};

export default MessageThread;
